



















































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import * as _ from 'lodash'
import { Subscription as RxSubscripton } from 'rxjs'
import PaymentCardComponent from '@/components/PaymentCardComponent.vue'
import { User, UserData } from '@/core/Types'
import * as db from '@/core/Database'
import appManager from '@/services/AppManager'
import { QuerySnapshot } from '@/extern/FirebaseTypes'
import Spinner from '@/components/Spinner.vue'
import { RoutePaths } from '@/router'
import AddSubscriptionPanel from '@/components/AddSubscriptionPanel.vue'
import { SubscriptionInterval, SubscriptionStatusTypes } from '@/extern/StripeTypes'
import { getDateTextFromMillis, getSubscriptionStatusColor, getSubscriptionStatusText } from '@/model/Helpers'
import EditLicenseCountDialog from '@/components/subscriptions/EditLicenseCountDialog.vue'
import { Subscription } from '@/core/ApiTypes'
import SubscriptionPeriodEndComponent from '@/components/subscriptions/SubscriptionPeriodEndComponent.vue'
import { ItemEvent, ItemEventType } from '@/model/EventTypes'

@Component({
    components: {
        Spinner,
        AddSubscriptionPanel,
        PaymentCardComponent,
        EditLicenseCountDialog,
        SubscriptionPeriodEndComponent
    }
})
export default class SubscriptionsView extends Vue {
    isLoadingCompany: boolean = true
    companyId: string|undefined
    stripeCustomerId: string|undefined
    usersSnapshotCounter: number = 0 // simple way to force a refresh when new items arise
    users: User[] = []
    usersUnsubscriber: VoidFunction|null = null

    // subscriptions
    isLoadingSubscriptions: boolean = false
    subscriptions: Subscription[] = []
    subscriptionEventSubscription: RxSubscripton|null = null
    subscriptionsSnapshotCounter: number = 0
    hasMoreSubscriptions: boolean = false

    // edit license count dialog
    isEditLicenseCountDialogVisible: boolean = false
    editLicenseCountDialogCounter: number = 0 // simple way to reset the dialog each time it is opened
    editLicenseCountSubscription: Subscription|null = null

    // cancel autorenew subscription dialog
    isCancelSubscriptoinDialogVisible: boolean = false
    cancelSubscriptionDialogCounter: number = 0 // simple way to reset the dialog each time it is opened
    cancelSubscription: Subscription|null = null
    isCancellingSubscription: boolean = false

    // noinspection JSUnusedGlobalSymbols
    async created() {
        const user = appManager.userManager.state?.user
        if (user && user.data.companyId) {
            this.usersUnsubscriber = db.streamUsers(
                { companyId: user.data.companyId },
                snapshot => this.onUsersReceived(snapshot),
                error => this.onUsersError(error)
            )

            const company = await db.getCompanyData(user.data.companyId)
            this.companyId = user.data.companyId
            this.stripeCustomerId = company?.stripeCustomerId
            this.isLoadingCompany = false

            // listen for subscription changes from other components
            this.subscriptionEventSubscription = appManager.subscriptionManager.eventStream.subscribe(
                event => this.onSubscriptionEventReceived(event)
            )

            if (company?.stripeCustomerId)
                await this.loadMoreSubscriptions()
        }
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        if (this.usersUnsubscriber)
            this.usersUnsubscriber()
        if (this.subscriptionEventSubscription)
            this.subscriptionEventSubscription.unsubscribe()
    }

    onSubscriptionEventReceived(event: ItemEvent<Subscription>) {
        const index = this.subscriptions.findIndex(s => s.id === event.item.id)

        if (event.type === ItemEventType.ADDED) {
            if (index < 0) {
                this.subscriptions.splice(0, 0, event.item)
                this.subscriptionsSnapshotCounter += 1
            }
        } else if (event.type === ItemEventType.CHANGED) {
            if (index >= 0) {
                this.subscriptions[index] = event.item
                this.subscriptionsSnapshotCounter += 1
            }
        } else if (event.type === ItemEventType.REMOVED) {
            if (index >= 0) {
                this.subscriptions.splice(index, 1)
                this.subscriptionsSnapshotCounter += 1
            }
        }
    }

    async loadMoreSubscriptions(): Promise<void> {
        this.isLoadingSubscriptions = true

        try {
            const response = await appManager.subscriptionManager.querySubscriptions({
                companyId: this.companyId,
                stripeCustomerId: this.stripeCustomerId,
                startAfterStripeSubscriptionId: _.last(this.subscriptions)?.id,
                includeCanceled: true
            })
            this.subscriptions.push(...response.subscriptions)
            this.hasMoreSubscriptions = response.hasMore
            this.subscriptionsSnapshotCounter += 1
        } catch (error) {
            console.error(`Error Loading Subscriptions: ${error}`)
        }

        this.isLoadingSubscriptions = false
    }

    getWindowIndex(): number {
        return this.$route.query['add'] ? 1 : 0
    }

    getSubscriptionStatusColorLocal(subscription: Subscription): string {
        return getSubscriptionStatusColor(subscription)
    }

    getSubscriptionStatusTextLocal(subscription: Subscription): string {
        return getSubscriptionStatusText(subscription)
    }

    getDateTextLocal(millis: number): string {
        return getDateTextFromMillis(millis)
    }

    getIntervalText(interval: SubscriptionInterval): string {
        switch (interval) {
            case 'day':
                return 'Daily';
            case 'month':
                return 'Monthly';
            case 'week':
                return 'Weekly';
            case 'year':
                return 'Yearly';
        }

        return '---'
    }

    getSubscriptionUserCount(subscription: Subscription): number {
        let count = 0
        if (subscription) {
            for (const user of this.users) {
                if (user.data.stripeSubscriptionIds?.includes(subscription.id))
                    ++count
            }
        }
        return count
    }

    isCanceled(subscription: Subscription): boolean {
        return subscription?.data?.status === SubscriptionStatusTypes.canceled
    }

    isCancellable(subscription: Subscription): boolean {
        return !subscription?.data?.cancelAtMillis &&
            !subscription?.data?.cancelAtPeriodEnd &&
            !subscription?.data?.canceledAtMillis
    }

    onUsersReceived(snapshot: QuerySnapshot) {
        this.users = snapshot.docs
            .filter(doc => doc.exists)
            .map(doc => {
                return {
                    id: doc.id,
                    data: doc.data() as UserData
                }
            })

        this.usersSnapshotCounter += 1
    }

    onUsersError(error: Error) {
        console.error(`Error receiving users: ${error}`)
    }

    onAddSubscriptionPressed() {
        this.$router.push({
            path: RoutePaths.SUBSCRIPTIONS,
            query: {
                add: '1'
            }
        })
    }

    onEditLicenseCountPressed(subscription: Subscription) {
        this.editLicenseCountSubscription = subscription
        this.editLicenseCountDialogCounter += 1
        this.isEditLicenseCountDialogVisible = true
    }

    onCloseEditLicenseCountDialogRequested() {
        this.isEditLicenseCountDialogVisible = false
    }

    onCancelSubscriptionPressed(subscription: Subscription) {
        this.cancelSubscription = subscription
        this.cancelSubscriptionDialogCounter += 1
        this.isCancelSubscriptoinDialogVisible = true
        this.isCancellingSubscription = false
    }

    async onCancelSubscriptionConfirmed() {
        if (!this.cancelSubscription)
            return

        this.isCancellingSubscription = true

        try {
            await appManager.subscriptionManager.cancelSubscription({
                stripeSubscriptionId: this.cancelSubscription.id,
                stripeCustomerId: this.cancelSubscription.data.stripeCustomerId
            })
        } catch (error) {
            console.error(`Error Canceling Subscription: ${error}`)
        }

        this.isCancellingSubscription = false
        this.isCancelSubscriptoinDialogVisible = false
    }
}
